import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { RunStatusBadge } from "@/components/run-status-badge";
import {
  formatNumber,
  formatNumberFull,
  formatPercent,
  formatReportDate,
  formatPercentage,
} from "@/lib/format";
import type { DailyRun } from "@/lib/types";

interface RunsTableProps {
  runs: DailyRun[];
  loading?: boolean;
  emptyHint?: string;
}

export function RunsTable({ runs, loading, emptyHint }: RunsTableProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-baseline justify-between pb-2">
        <CardTitle className="text-base">Run history</CardTitle>
        <p className="text-xs text-muted-foreground">
          Click a row to drill into sampled posts and classifications.
        </p>
      </CardHeader>
      <CardContent className="px-0">
        <Table>
          <TableHeader>
            <TableRow className="border-border/60 hover:bg-transparent">
              <TableHead className="pl-6">Report date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Percentage</TableHead>
              <TableHead className="text-right">Antisem / Analyzed</TableHead>
              <TableHead className="text-right">% antisemitic posts</TableHead>
              <TableHead className="text-right">Estimated</TableHead>
              <TableHead className="text-right">Total matching</TableHead>
              <TableHead className="pr-6 text-right" aria-label="View" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, idx) => (
                <TableRow key={idx} className="border-border/40">
                  {Array.from({ length: 8 }).map((__, ci) => (
                    <TableCell
                      key={ci}
                      className={ci === 0 ? "pl-6" : ci === 7 ? "pr-6" : ""}
                    >
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : runs.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="px-6 py-12 text-center text-sm text-muted-foreground"
                >
                  {emptyHint ?? "No runs to show yet."}
                </TableCell>
              </TableRow>
            ) : (
              runs.map((run) => (
                <TableRow key={run.id} className="border-border/40">
                  <TableCell className="pl-6 font-medium">
                    <Link
                      to={`/runs/${run.id}`}
                      className="hover:text-foreground"
                    >
                      {formatReportDate(run.report_date)}
                    </Link>
                    <div className="text-xs text-muted-foreground">
                      {run.report_timezone}
                    </div>
                  </TableCell>
                  <TableCell>
                    <RunStatusBadge status={run.status} />
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatPercentage(run.daily_score)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    <span className="text-foreground">
                      {formatNumberFull(run.antisemitic_sample_count)}
                    </span>
                    <span className="text-muted-foreground">
                      {" "}
                      / {formatNumberFull(run.sample_size_analyzed)}
                    </span>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatPercent(run.antisemitic_sample_rate)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatNumber(run.estimated_antisemitic_post_count)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatNumber(run.total_matching_post_count)}
                  </TableCell>
                  <TableCell className="pr-6 text-right">
                    <Link
                      to={`/runs/${run.id}`}
                      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                    >
                      View
                      <ArrowUpRight className="h-3 w-3" />
                    </Link>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
